// frontend/src/pages/technician/TechnicianInventory.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Paper,
    Alert,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Badge
} from '@mui/material';
import {
    Inventory,
    Category,
    Build,
    Warning,
    Error as ErrorIcon,
    ExpandMore,
    QrCode,
    Assignment,
    Refresh,
    ReportProblem,
    CheckCircle,
    Info
} from '@mui/icons-material';
import { technicianApiService } from '../../services/technicianApiService';

// Función helper para convertir valores a números seguros
const safeNumber = (value, defaultValue = 0) => {
    const num = parseFloat(value);
    return isNaN(num) ? defaultValue : num;
};

const TechnicianInventory = () => {
    const [inventory, setInventory] = useState([]);
    const [serializedInventory, setSerializedInventory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [damageDialog, setDamageDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [damageReport, setDamageReport] = useState({
        quantity_affected: 1,
        damage_description: '',
        damage_type: 'broken'
    });
    const [stats, setStats] = useState({
        total_items: 0,
        total_value: 0,
        categories: {}
    });
    const [detailDialog, setDetailDialog] = useState(false);
    const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);

    useEffect(() => {
        loadInventory();
    }, []);

    const loadInventory = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Cargar inventario general y serializado en paralelo
            const [generalData, serializedData] = await Promise.all([
                technicianApiService.getAssignedInventory(),
                technicianApiService.getSerializedInventory()
            ]);

            console.log('Datos recibidos general:', generalData);
            console.log('Datos recibidos serializado:', serializedData);

            // Procesar inventario con conversión segura de números
            const inventoryItems = generalData.inventory ? generalData.inventory.map(item => ({
                ...item,
                quantity: safeNumber(item.quantity, 0),
                purchase_price: safeNumber(item.purchase_price, 0),
                selling_price: safeNumber(item.selling_price, 0)
            })) : [];

            setInventory(inventoryItems);
            setSerializedInventory(serializedData.serialized || []);

            // Calcular estadísticas con valores numéricos seguros
            const stats = {
                total_items: safeNumber(generalData.total_items, 0),
                total_value: safeNumber(generalData.total_value, 0),
                categories: inventoryItems.reduce((acc, item) => {
                    const category = item.category_name || 'Sin categoría';
                    if (!acc[category]) {
                        acc[category] = { count: 0, items: [] };
                    }
                    acc[category].count += item.quantity;
                    acc[category].items.push(item);
                    return acc;
                }, {})
            };

            setStats(stats);
        } catch (error) {
            console.error('Error cargando inventario:', error);
            setError(error.response?.data?.message || 'Error cargando inventario');
        } finally {
            setLoading(false);
        }
    };

    const refreshData = async () => {
        try {
            setRefreshing(true);
            await loadInventory();
        } catch (error) {
            console.error('Error refrescando datos:', error);
        } finally {
            setRefreshing(false);
        }
    };

    const openDamageReport = (item) => {
        setSelectedItem(item);
        setDamageReport({
            quantity_affected: 1,
            damage_description: '',
            damage_type: 'broken'
        });
        setDamageDialog(true);
    };

    const submitDamageReport = async () => {
        if (!selectedItem || !damageReport.damage_description.trim()) {
            setError('Por favor complete todos los campos requeridos');
            return;
        }

        try {
            await technicianApiService.reportDamagedEquipment(selectedItem.item_id, {
                quantity_affected: damageReport.quantity_affected,
                damage_description: damageReport.damage_description,
                damage_type: damageReport.damage_type
            });

            setDamageDialog(false);
            setSelectedItem(null);
            await refreshData();
            setError(null);

        } catch (error) {
            console.error('Error reportando daño:', error);
            setError(error.response?.data?.message || 'Error reportando equipo dañado');
        }
    };

    const showItemDetails = (item) => {
        setSelectedInventoryItem(item);
        setDetailDialog(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'assigned': return 'success';
            case 'damaged': return 'error';
            case 'returned': return 'default';
            default: return 'info';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'assigned': return 'Asignado';
            case 'damaged': return 'Dañado';
            case 'returned': return 'Devuelto';
            default: return status;
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h5" gutterBottom>
                            Mi Inventario
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Equipos asignados para instalaciones y mantenimiento
                        </Typography>
                    </Box>
                    <IconButton onClick={refreshData} disabled={refreshing}>
                        <Refresh />
                    </IconButton>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Estadísticas */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Inventory color="primary" sx={{ mr: 2, fontSize: 40 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total de Equipos
                                    </Typography>
                                    <Typography variant="h4">
                                        {stats.total_items}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Category color="info" sx={{ mr: 2, fontSize: 40 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Categorías
                                    </Typography>
                                    <Typography variant="h4">
                                        {Object.keys(stats.categories).length}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Assignment color="success" sx={{ mr: 2, fontSize: 40 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Valor Total
                                    </Typography>
                                    <Typography variant="h4">
                                        S/. {safeNumber(stats.total_value).toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Inventario por categorías */}
            {Object.keys(stats.categories).length > 0 ? (
                <Box>
                    {Object.entries(stats.categories).map(([category, data]) => (
                        <Accordion key={category} defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box display="flex" alignItems="center" width="100%">
                                    <Category sx={{ mr: 2 }} />
                                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                        {category}
                                    </Typography>
                                    <Badge badgeContent={data.count} color="primary">
                                        <Chip label={`${data.items.length} tipo${data.items.length !== 1 ? 's' : ''}`} />
                                    </Badge>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Equipo</TableCell>
                                                <TableCell>Modelo/Marca</TableCell>
                                                <TableCell align="center">Cantidad</TableCell>
                                                <TableCell align="center">Estado</TableCell>
                                                <TableCell align="center">Valor Unit.</TableCell>
                                                <TableCell align="center">Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.items.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="subtitle2">
                                                                {item.item_name}
                                                            </Typography>
                                                            {item.sku && (
                                                                <Typography variant="caption" color="textSecondary">
                                                                    SKU: {item.sku}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {item.brand} {item.model}
                                                        </Typography>
                                                        {item.description && (
                                                            <Typography variant="caption" color="textSecondary">
                                                                {item.description}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={safeNumber(item.quantity)}
                                                            color={safeNumber(item.quantity) > 0 ? 'success' : 'default'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={getStatusText(item.status)}
                                                            color={getStatusColor(item.status)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        S/. {safeNumber(item.purchase_price).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box display="flex" gap={1} justifyContent="center">
                                                            <IconButton
                                                                size="small"
                                                                color="info"
                                                                onClick={() => showItemDetails(item)}
                                                                title="Ver detalles"
                                                            >
                                                                <Info />
                                                            </IconButton>
                                                            {item.status === 'assigned' && safeNumber(item.quantity) > 0 && (
                                                                <IconButton
                                                                    size="small"
                                                                    color="warning"
                                                                    onClick={() => openDamageReport(item)}
                                                                    title="Reportar daño"
                                                                >
                                                                    <ReportProblem />
                                                                </IconButton>
                                                            )}
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                title="Escanear QR"
                                                            >
                                                                <QrCode />
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            ) : (
                <Alert severity="info">
                    No tienes equipos asignados actualmente. Contacta con el administrador para solicitar equipos.
                </Alert>
            )}

            {/* Información adicional */}
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Información Importante
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircle color="success" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Responsabilidad"
                                secondary="Eres responsable de todos los equipos asignados. Manténlos en buen estado."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Warning color="warning" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Reportar Daños"
                                secondary="Reporta inmediatamente cualquier daño o pérdida de equipos."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Build color="info" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Uso Profesional"
                                secondary="Los equipos deben usarse únicamente para instalaciones y mantenimiento autorizados."
                            />
                        </ListItem>
                    </List>
                </CardContent>
            </Card>

            {/* Dialog para reportar daño */}
            <Dialog
                open={damageDialog}
                onClose={() => setDamageDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <ReportProblem color="warning" />
                        Reportar Equipo Dañado
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedItem && (
                        <Box>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Estás reportando un problema con: <strong>{selectedItem.item_name}</strong>
                            </Alert>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Cantidad afectada"
                                        type="number"
                                        value={damageReport.quantity_affected}
                                        onChange={(e) => setDamageReport(prev => ({
                                            ...prev,
                                            quantity_affected: Math.min(
                                                parseInt(e.target.value) || 1,
                                                safeNumber(selectedItem.quantity, 1)
                                            )
                                        }))}
                                        inputProps={{
                                            min: 1,
                                            max: safeNumber(selectedItem.quantity, 1)
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Tipo de daño</InputLabel>
                                        <Select
                                            value={damageReport.damage_type}
                                            onChange={(e) => setDamageReport(prev => ({
                                                ...prev,
                                                damage_type: e.target.value
                                            }))}
                                            label="Tipo de daño"
                                        >
                                            <MenuItem value="broken">Roto</MenuItem>
                                            <MenuItem value="lost">Perdido</MenuItem>
                                            <MenuItem value="stolen">Robado</MenuItem>
                                            <MenuItem value="malfunction">Mal funcionamiento</MenuItem>
                                            <MenuItem value="wear">Desgaste normal</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Descripción del daño"
                                        value={damageReport.damage_description}
                                        onChange={(e) => setDamageReport(prev => ({
                                            ...prev,
                                            damage_description: e.target.value
                                        }))}
                                        placeholder="Describe detalladamente qué pasó con el equipo, cuándo ocurrió, etc."
                                        required
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDamageDialog(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={submitDamageReport}
                        variant="contained"
                        color="warning"
                        disabled={!damageReport.damage_description.trim()}
                    >
                        Reportar Daño
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para ver detalles del equipo */}
            <Dialog
                open={detailDialog}
                onClose={() => setDetailDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Info color="primary" />
                        Detalles del Equipo
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedInventoryItem && (() => {
                        // Buscar items serializados correspondientes a este item_id
                        const matchedSerials = serializedInventory.filter(
                            ser => ser.item_id === selectedInventoryItem.item_id
                        );
                        
                        // Obtener especificaciones del primer item serializado encontrado (si existe)
                        const specSource = matchedSerials[0] || {};
                        const hasSpecs = specSource.equipment_type || specSource.frequency || specSource.speed_capacity || specSource.hardware_firmware_version;

                        return (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom color="primary">
                                        {selectedInventoryItem.item_name}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        {selectedInventoryItem.description || 'Sin descripción detallada.'}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Marca / Modelo</Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedInventoryItem.brand || 'N/A'} {selectedInventoryItem.model || 'N/A'}
                                    </Typography>

                                    <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1.5 }}>Categoría</Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedInventoryItem.category_name || 'Sin categoría'}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">SKU</Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {selectedInventoryItem.sku || 'N/A'}
                                    </Typography>

                                    <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1.5 }}>Cantidad Disponible en Mano</Typography>
                                    <Chip
                                        label={`${selectedInventoryItem.quantity} unidades`}
                                        color={selectedInventoryItem.quantity > 0 ? 'success' : 'default'}
                                        size="small"
                                        sx={{ mt: 0.5 }}
                                    />
                                </Grid>

                                {hasSpecs && (
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="subtitle1" gutterBottom color="secondary" sx={{ fontWeight: 'medium' }}>
                                            Especificaciones Técnicas
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                                            {specSource.equipment_type && (
                                                <Chip
                                                    label={`Tipo: ${specSource.equipment_type}`}
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            )}
                                            {specSource.frequency && (
                                                <Chip
                                                    label={`Frecuencia: ${specSource.frequency}`}
                                                    variant="outlined"
                                                    color="secondary"
                                                />
                                            )}
                                            {specSource.speed_capacity && (
                                                <Chip
                                                    label={`Capacidad: ${specSource.speed_capacity}`}
                                                    variant="outlined"
                                                    color="info"
                                                />
                                            )}
                                            {specSource.hardware_firmware_version && (
                                                <Chip
                                                    label={`Firmware/HW: ${specSource.hardware_firmware_version}`}
                                                    variant="outlined"
                                                    color="default"
                                                />
                                            )}
                                        </Box>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle1" gutterBottom color="primary" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <QrCode fontSize="small" />
                                        Equipos Serializados Asignados ({matchedSerials.length})
                                    </Typography>

                                    {matchedSerials.length > 0 ? (
                                        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1.5, maxHeight: 250 }}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>N°</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Número de Serie (S/N)</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Dirección MAC</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Condición</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Fecha Asignación</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {matchedSerials.map((ser, index) => (
                                                        <TableRow key={ser.id} hover>
                                                            <TableCell>{index + 1}</TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                                                    {ser.serial_number}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                    {ser.mac_address || 'N/A'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={ser.condition === 'new' ? 'Nuevo' : (ser.condition === 'used' ? 'Usado' : ser.condition)}
                                                                    color={ser.condition === 'new' ? 'success' : 'warning'}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {ser.assigned_to_tech_date ? new Date(ser.assigned_to_tech_date).toLocaleDateString() : 'N/A'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Alert severity="info" sx={{ mt: 1 }}>
                                            Este artículo se administra de forma volumétrica. No requiere números de serie individuales ni direcciones MAC.
                                        </Alert>
                                    )}
                                </Grid>
                            </Grid>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialog(false)} variant="contained" color="primary">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TechnicianInventory;